// PoleSelection.js
import React, { useState, useEffect } from 'react';
import '../FishFinderBuilder.css';
import { polesData } from '../data/productData';
import ImageModal from './ImageModal';

const PoleSelection = ({
  pole = null,
  setPole = () => {},
  prevStep = () => {},
  nextStep = () => {},
  resetAll = () => {},
  isInModal = false,
  onNavigateToFullPage = () => {}
}) => {
  // Track which images have already tried fallbacks
  const [failedImages, setFailedImages] = useState({});
  
  // Add modal state
  const [modalImage, setModalImage] = useState({
    isOpen: false,
    url: '',
    alt: ''
  });

  // Track selected pole
  const [selectedPoleId, setSelectedPoleId] = useState(pole?.id || null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add modal handlers
  const openImageModal = (imageUrl, altText) => {
    setModalImage({
      isOpen: true,
      url: imageUrl,
      alt: altText
    });
  };
  
  const closeImageModal = () => {
    setModalImage({
      isOpen: false,
      url: '',
      alt: ''
    });
  };

  // Handle image errors with fallback images
  const handleImageError = (e, poleName) => {
    if (failedImages[poleName]) return;
    
    console.error(`Image failed to load for ${poleName}: ${e.target.src}`);
    setFailedImages(prev => ({...prev, [poleName]: true}));
    
    const poleData = polesData.find(p => p.name === poleName);
    
    if (poleData && poleData.fallbackImage) {
      e.target.src = poleData.fallbackImage;
      return;
    }
    
    e.target.src = `https://dummyimage.com/150x100/cccccc/000000&text=${poleName.replace(/\s+/g, '+')}`;
    e.target.onerror = null;
  };

  // Handle selection of a pole
  const handlePoleSelect = (selectedPole) => {
    setSelectedPoleId(selectedPole.id);
    setPole(selectedPole);
  };

  const renderPoleCard = (poleOption) => {
    const isSelected = selectedPoleId === poleOption.id;

    return (
      <>
        <div 
          key={poleOption.id}
          className="battery-card" 
          onClick={() => handlePoleSelect(poleOption)}
          style={{
            backgroundColor: '#1a2634',
            borderRadius: '8px',
            padding: '15px',
            color: 'white',
            marginBottom: isSelected ? '8px' : '12px',
            border: isSelected ? '2px solid #4CAF50' : '1px solid rgba(255, 255, 255, 0.1)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            transform: isSelected ? 'scale(1.01)' : 'scale(1)',
            boxShadow: isSelected ? '0 4px 12px rgba(0,0,0,0.2)' : 'none',
            position: 'relative'
          }}
        >
          {/* Selected indicator */}
          {isSelected && (
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              backgroundColor: '#4CAF50',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '0.8em',
              fontWeight: '500'
            }}>
              Selected ✓
            </div>
          )}

          <h2 style={{ 
            marginBottom: '6px', 
            fontSize: '1.2em',
            fontWeight: '500'
          }}>{poleOption.name}</h2>
          <p style={{ 
            marginBottom: '12px', 
            color: '#b8c7d9',
            fontSize: '0.95em'
          }}>MSRP: ${poleOption.msrp.toFixed(2)}</p>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '120px 1fr', 
            gap: '15px', 
            marginBottom: '15px',
            alignItems: 'start'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              backgroundColor: 'white',
              borderRadius: '4px',
              padding: '8px',
              height: '100px',
              justifyContent: 'center'
            }}>
              <img
                src={poleOption.image}
                alt={poleOption.name}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain',
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  openImageModal(poleOption.image, poleOption.name);
                }}
                onError={(e) => handleImageError(e, poleOption.name)}
              />
            </div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'auto 1fr', 
              gap: '4px 12px', 
              alignContent: 'start',
              fontSize: '0.9em'
            }}>
              <div style={{ color: '#b8c7d9' }}>Length:</div>
              <div>{poleOption.displaySpecs["Length"]}</div>
              <div style={{ color: '#b8c7d9' }}>Type:</div>
              <div>{poleOption.displaySpecs["Type"]}</div>
              <div style={{ color: '#b8c7d9' }}>Handle:</div>
              <div>{poleOption.displaySpecs["Handle"]}</div>
              <div style={{ color: '#b8c7d9' }}>Arm Size:</div>
              <div>{poleOption.displaySpecs["Arm Size"]}</div>
              <div style={{ color: '#b8c7d9' }}>Base Size:</div>
              <div>{poleOption.displaySpecs["Base Size"]}</div>
              <div style={{ color: '#b8c7d9' }}>Adjustable:</div>
              <div>{poleOption.displaySpecs["Adjustable"]}</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '12px'
          }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open('https://beatdownoutdoors.com/products/breakaway-transducer-pole', '_blank');
              }}
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: '#0066cc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.95em'
              }}
            >
              Find the Best Price!
            </button>
          </div>
        </div>
        
        {isSelected && (
          <button
            onClick={nextStep}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.95em',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            Continue with Build
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{ marginTop: '1px' }}
            >
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        )}
      </>
    );
  };

  return (
    <div style={{ 
      flex: 1,
      background: '#1a1a1a',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px'
    }}>
      {/* Back Button - Top */}
      <button
        onClick={prevStep}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'rgba(0, 0, 0, 0.4)',
          border: 'none',
          color: '#fff',
          fontSize: '14px',
          cursor: 'pointer',
          padding: '8px 16px',
          borderRadius: '4px',
          marginBottom: '24px',
          width: 'fit-content'
        }}
      >
        ← Back to Builder
      </button>

      <h2>Pole Selection</h2>
      <p>Choose a pole mount for your fish finder setup</p>

      {/* Main Content */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '20px',
        width: '100%'
      }}>
        {polesData.map((poleOption) => renderPoleCard(poleOption))}
      </div>

      {/* Bottom Buttons */}
      <div style={{ 
        display: 'flex',
        gap: '12px',
        marginTop: '24px'
      }}>
        <button
          onClick={prevStep}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(0, 0, 0, 0.4)',
            border: 'none',
            color: '#fff',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '8px 16px',
            borderRadius: '4px'
          }}
        >
          ← Back to Builder
        </button>
        <button
          onClick={resetAll}
          style={{
            background: 'rgba(0, 0, 0, 0.4)',
            border: 'none',
            color: '#fff',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '8px 16px',
            borderRadius: '4px'
          }}
        >
          Reset Selections
        </button>
      </div>

      <ImageModal 
        isOpen={modalImage.isOpen}
        imageUrl={modalImage.url}
        altText={modalImage.alt}
        onClose={closeImageModal}
      />
    </div>
  );
};

export default PoleSelection;
